import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { useCookieConsent } from '../context/CookieConsentContext';

const CookieBanner = () => {
    const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent();
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState({
        analytics: true,
        marketing: true
    });
    const [isMinimized, setIsMinimized] = useState(false);

    if (!showBanner) return null;

    return (
        <AnimatePresence>
            {!isMinimized && (
                <motion.div
                    initial={{ y: 50, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 50, opacity: 0, scale: 0.95 }}
                    className="fixed bottom-4 left-4 z-[9999] w-[calc(100%-2rem)] md:w-[400px]"
                >
                    <div className="bg-[#111] border border-white/10 rounded-xl shadow-2xl overflow-hidden backdrop-blur-md">
                        {!showSettings ? (
                            // --- VUE PRINCIPALE COMPACTE ---
                            <div className="p-5">
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="w-10 h-10 bg-[#222] rounded-lg flex items-center justify-center flex-shrink-0 animate-bounce-slow">
                                        <Cookie className="text-[#00f2ea]" size={20} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-bold text-white mb-1">
                                            On respecte votre vie privée
                                        </h3>
                                        <p className="text-xs text-gray-400 leading-relaxed">
                                            Nous utilisons des cookies pour améliorer votre expérience. C'est tout.
                                            <button
                                                onClick={() => setShowSettings(true)}
                                                className="ml-2 underline text-gray-500 hover:text-white transition-colors"
                                            >
                                                Paramétrer
                                            </button>
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={rejectAll}
                                        className="flex-1 py-2 px-3 rounded-lg text-xs font-bold text-gray-400 hover:bg-white/5 hover:text-white transition-colors border border-transparent hover:border-white/10"
                                    >
                                        Non merci
                                    </button>
                                    <button
                                        onClick={acceptAll}
                                        className="flex-[2] py-2 px-3 rounded-lg text-xs font-bold bg-white text-black hover:bg-[#00f2ea] transition-colors shadow-lg flex items-center justify-center gap-2"
                                    >
                                        <Check size={14} />
                                        C'est OK pour moi
                                    </button>
                                </div>
                            </div>
                        ) : (
                            // --- VUE PARAMÈTRES (Compacte) ---
                            <div className="p-5">
                                <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <Settings size={14} className="text-[#00f2ea]" />
                                        Préférences
                                    </h3>
                                    <button
                                        onClick={() => setShowSettings(false)}
                                        className="text-gray-500 hover:text-white"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                <div className="space-y-3 mb-5">
                                    {/* Item */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Shield size={14} className="text-green-500" />
                                            <span className="text-xs text-white">Essentiels</span>
                                        </div>
                                        <div className="w-8 h-4 bg-green-900 rounded-full relative opacity-50">
                                            <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-green-500 rounded-full"></div>
                                        </div>
                                    </div>

                                    {/* Item */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-300">Analytique</span>
                                        </div>
                                        <button
                                            onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                                            className={`w-8 h-4 rounded-full relative transition-colors ${preferences.analytics ? 'bg-[#00f2ea]/20' : 'bg-gray-800'}`}
                                        >
                                            <div className={`absolute top-0.5 w-3 h-3 rounded-full transition-all ${preferences.analytics ? 'right-0.5 bg-[#00f2ea]' : 'left-0.5 bg-gray-600'}`}></div>
                                        </button>
                                    </div>

                                    {/* Item */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-300">Marketing</span>
                                        </div>
                                        <button
                                            onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                                            className={`w-8 h-4 rounded-full relative transition-colors ${preferences.marketing ? 'bg-[#ff0050]/20' : 'bg-gray-800'}`}
                                        >
                                            <div className={`absolute top-0.5 w-3 h-3 rounded-full transition-all ${preferences.marketing ? 'right-0.5 bg-[#ff0050]' : 'left-0.5 bg-gray-600'}`}></div>
                                        </button>
                                    </div>
                                </div>

                                <button
                                    onClick={() => savePreferences(preferences)}
                                    className="w-full py-2 bg-[#111] border border-white/20 hover:bg-white hover:text-black hover:border-white text-white text-xs font-bold rounded-lg transition-all"
                                >
                                    Enregistrer
                                </button>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CookieBanner;
